// SmartStays Automation-paneel: enkel zichtbaar voor verhuurders met het
// aparte "SmartStays Automation"-abonnement (User.hasAutomation, zie
// backend/index.js /api/automation-overview). Toont bezetting, kanaalmix en
// aankomende in-/uitchecks, berekend uit onze eigen boekingsdata (werkt dus
// al ongeacht of de live Guesty Open API-koppeling zelf al actief is) - plus
// een eerlijke melding zodra financiële/omzetcijfers nog niet beschikbaar
// zijn omdat die wél de echte Open API-koppeling vereisen.
import React, { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import API_URL from '../config';
import PlatformBadge from './PlatformBadge';
import { PropertyFilter, usePropertySelection } from './PropertyFilter';
import { getAuthToken } from '../utils/authSession';
import { cacheGet, cacheSet } from '../utils/dataCache';
import './AutomationOverview.css';

const CHANNEL_LABELS = {
  airbnb: 'Airbnb',
  booking_com: 'Booking.com',
  vrbo: 'Vrbo',
  direct: 'Direct',
  onbekend: null, // vertaald via i18n hieronder
};

// "propertyId" is optioneel: zonder dat prop toont dit paneel het overkoepelende
// overzicht voor alle woningen samen (zoals bij "Overzicht" > "Automation" in
// de sidebar); met propertyId (vanuit een woning z'n eigen Automation-tab)
// toont het exact dezelfde soort inzichten, maar geschaald naar die ene woning.
function AutomationOverview({ propertyId, propertyName, properties } = {}) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Met "properties" (lijst van de verhuurder) kan de gebruiker kiezen welke
  // woningen meetellen: alle samen (standaard) of een eigen selectie. Enkel
  // woningen met Automation aan komen in de keuze (valt terug op alle
  // woningen als er geen enkele expliciet is ingeschakeld).
  const filterList = useMemo(() => {
    if (!properties) return [];
    const enabled = properties.filter((p) => p.automationEnabled);
    return enabled.length > 0 ? enabled : properties;
  }, [properties]);
  const { selected, setSelected, selectedProperties } = usePropertySelection('ss_filter_automation_v2', filterList, false);
  const filtering = !propertyId && !!properties;
  const noneSelected = filtering && selected.length === 0;
  const effectivePropertyId = propertyId
    || (filtering && selected.length > 0 && selected.length < properties.length
      ? selectedProperties.map((p) => p.id).join(',')
      : undefined);

  const cacheKeyAuto = `automation|${effectivePropertyId || 'all'}`;
  // Eerder opgehaalde cijfers meteen tonen (geen "Laden..." bij elke tabwissel).
  useLayoutEffect(() => {
    const hit = cacheGet(cacheKeyAuto);
    if (hit) { setData(hit); setLoading(false); }
  }, [cacheKeyAuto]);

  useEffect(() => {
    if (noneSelected) { setLoading(false); return; }
    const fetchOverview = async () => {
      if (!cacheGet(cacheKeyAuto)) setLoading(true);
      try {
        const token = getAuthToken();
        const response = await axios.get(`${API_URL}/api/automation-overview`, {
          headers: { Authorization: `Bearer ${token}` },
          params: effectivePropertyId ? { propertyId: effectivePropertyId } : {}
        });
        setData(response.data);
        cacheSet(cacheKeyAuto, response.data);
        setError('');
      } catch (err) {
        setError(err.response?.data?.error || t('automation.loadError'));
      } finally {
        setLoading(false);
      }
    };
    fetchOverview();
  }, [t, effectivePropertyId, noneSelected, cacheKeyAuto]);

  const filterBar = !propertyId && properties
    ? <PropertyFilter properties={filterList} selected={selected} onChange={setSelected} />
    : null;

  if (noneSelected) {
    return <div className="automation-overview">{filterBar}</div>;
  }

  if (loading && !data) {
    return <div className="automation-overview">{filterBar}<p className="automation-loading" aria-busy="true" /></div>;
  }

  if (error || !data) {
    return <div className="automation-overview">{filterBar}<p className="automation-error">{error}</p></div>;
  }

  const { occupancy, channelMix, upcoming, revenue, revenueStatus, reviews, reviewsStatus, guestyStatus } = data;
  const totalChannelCount = channelMix.reduce((sum, c) => sum + c.count, 0);

  const formatDate = (iso) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  const formatCurrency = (amount, currency) => {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
    } catch (e) {
      return `${amount} ${currency}`;
    }
  };
  const revenueCurrencies = revenue ? Object.keys(revenue.totalsByCurrency) : [];

  return (
    <div className="automation-overview">
      {filterBar}
      <div className="automation-header">
        <h2>{propertyName ? t('automation.titleForProperty', { name: propertyName }) : t('automation.title')}</h2>
      </div>

      <div className="automation-cards">
        <div className="automation-card">
          <div className="automation-card-label">{t('automation.occupancyLabel')}</div>
          <div className="automation-card-value">{occupancy.rate}%</div>
          <div className="automation-card-hint">{t('automation.occupancyHint', { days: occupancy.windowDays })}</div>
        </div>

        <div className="automation-card automation-card-wide">
          <div className="automation-card-label">{t('automation.channelMixLabel')}</div>
          {channelMix.length === 0 ? (
            <div className="automation-card-hint">{t('automation.noBookings')}</div>
          ) : (
            <div className="automation-channel-bars">
              {channelMix.map((c) => {
                const pct = totalChannelCount > 0 ? Math.round((c.count / totalChannelCount) * 100) : 0;
                const label = CHANNEL_LABELS[c.source] || (c.source === 'onbekend' ? t('automation.channelUnknown') : c.source);
                return (
                  <div className="automation-channel-row" key={c.source}>
                    <span className="automation-channel-name">{label}</span>
                    <div className="automation-channel-bar-track">
                      <div className="automation-channel-bar-fill" style={{ width: `${pct}%` }}></div>
                    </div>
                    <span className="automation-channel-count">{pct}%</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="automation-card">
          <div className="automation-card-label">{t('automation.revenueTitle')}</div>
          {revenueStatus === 'available' ? (
            revenueCurrencies.length === 0 ? (
              <div className="automation-card-hint">{t('automation.revenueNoTransactions', { days: revenue.windowDays })}</div>
            ) : (
              <>
                <div className="automation-revenue-values">
                  {revenueCurrencies.map((currency) => (
                    <div className="automation-card-value automation-revenue-value" key={currency}>
                      {formatCurrency(revenue.totalsByCurrency[currency], currency)}
                    </div>
                  ))}
                </div>
                <div className="automation-card-hint">{t('automation.revenueHint', { days: revenue.windowDays })}</div>
              </>
            )
          ) : revenueStatus === 'guestyPayNotConfigured' ? (
            <div className="automation-card-hint">{t('automation.guestyPayNotConfigured')}</div>
          ) : revenueStatus === 'error' ? (
            <div className="automation-card-hint">{t('automation.revenueError')}</div>
          ) : guestyStatus.configured ? (
            <div className="automation-card-hint">{t('automation.revenueComingSoon')}</div>
          ) : (
            <div className="automation-card-hint">{t('automation.guestyNotConnected')}</div>
          )}
        </div>
      </div>

      <div className="automation-section">
        <h3>{t('automation.upcomingTitle')}</h3>
        {upcoming.length === 0 ? (
          <p className="automation-card-hint">{t('automation.noUpcoming')}</p>
        ) : (
          <ul className="automation-upcoming-list">
            {upcoming.map((ev) => (
              <li key={ev.id} className="automation-upcoming-item">
                <span className="automation-upcoming-property" title={ev.property_name}>{ev.property_name}</span>
                <span className="automation-upcoming-guest">
                  <PlatformBadge platform={ev.bookingSource || ev.platform} />
                  <span className="automation-upcoming-guest-name" title={ev.guestName || t('automation.unknownGuest')}>{ev.guestName || t('automation.unknownGuest')}</span>
                </span>
                <span className="automation-upcoming-dates">{formatDate(ev.checkIn)} → {formatDate(ev.checkOut)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="automation-section">
        <h3>{t('automation.reviewsTitle')}</h3>
        {reviewsStatus === 'available' ? (
          reviews.total === 0 ? (
            <p className="automation-card-hint">{t('automation.reviewsNoReviewsYet')}</p>
          ) : (
            <>
              <div className="automation-card-value">{reviews.average != null ? `${reviews.average.toFixed(1)} / 5` : '—'}</div>
              <div className="automation-card-hint">{t('automation.reviewsHint', { count: reviews.total })}</div>
              {reviews.perProperty.filter((p) => p.total > 0).length > 1 && (
                <ul className="automation-upcoming-list">
                  {reviews.perProperty.filter((p) => p.total > 0).map((p) => (
                    <li key={p.propertyId} className="automation-upcoming-item">
                      <span className="automation-upcoming-property">{p.propertyName}</span>
                      <span className="automation-upcoming-guest">{p.avg != null ? `${p.avg.toFixed(1)} / 5` : '—'}</span>
                      <span className="automation-upcoming-dates">{t('automation.reviewsCount', { count: p.total })}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )
        ) : reviewsStatus === 'noListingsLinked' ? (
          <p className="automation-card-hint">{t('automation.reviewsNoListingsLinked')}</p>
        ) : reviewsStatus === 'error' ? (
          <p className="automation-card-hint">{t('automation.reviewsError')}</p>
        ) : guestyStatus.configured ? (
          <p className="automation-card-hint">{t('automation.reviewsComingSoon')}</p>
        ) : (
          <p className="automation-card-hint">{t('automation.guestyNotConnected')}</p>
        )}
      </div>
    </div>
  );
}

export default AutomationOverview;
